// @ts-check
import { defaultLocale, defaultLocaleBundle } from './DefaultLocaleBundle';
import { bundleData } from './LoadBundles';

let currentLocaleBundle = defaultLocaleBundle;

export function initializeLocaleBundle(config) {
  const isOnPrem = config.globalContext.stack.isOnPrem;

  return isOnPrem
    ? initializeLocaleBundleOnPrem(config)
    : initializeLocaleBundleSaaS(config);
}

/**
 * For more details about onprem localization see
 * https://docs.google.com/document/d/1q62mDFhj_NqG3s5roEY8Y4npdKY0NW0EsGyQqxLv508/edit?tab=t.0
 */
async function initializeLocaleBundleOnPrem(config) {
  const locale = config.globalContext.user.UserProfile.Language;

  if (/[a-z]{2}-[A-Z]{2}/.test(locale)) {
    try {
      const messages = await (
        await fetch(`/admin/static/${locale.replace('-', '_')}.json`)
      ).json();
      const translation = await (
        await fetch(
          `/admin/static/editorDictionary_${locale.replace('-', '_')}.json`,
        )
      ).json();
      return buildLocaleBundle(
        locale,
        messages,
        { messages: {} },
        getDateTimeConfigurationFromConfigData(config),
        translation,
      );
    } catch (error) {
      /* eslint-disable no-console */
      console.log(
        `Failed to load custom locale "${locale}". Falling back to default.`,
      );
      console.log(error);
      /* eslint-enable no-console */
    }
  }

  return initializeLocaleBundleSaaS(config);
}

/**
 * @param {EnvConfig} config
 * @returns {Promise<any>}
 */
function initializeLocaleBundleSaaS(config) {
  const language = config.globalContext.user.UserProfile.Language;
  const supportedLanguage =
    getSupportedLocale(language, bundleData) || defaultLocale;

  // Force the async bundle require to resolve BEFORE this function returns so
  // that the language bundle is loaded prior to running code that uses i18n methods
  // such as `formatMessageAsString`. This should prevent errors in the console and
  // messageKey fallbacks showing in the UI due to the bundle not being loaded yet
  // when the code first runs.
  return Promise.all([
    bundleData[supportedLanguage](),
    window.pinataBundlePromise,
  ]).then(([churroBundle, pinataBundle]) => {
    return buildLocaleBundle(
      supportedLanguage,
      churroBundle,
      JSON.parse(pinataBundle),
      getDateTimeConfigurationFromConfigData(config),
    );
  });
}

/**
 * @param {string} language
 * @param {any} churroLanguageBundle
 * @param {any} pinataLanguageBundle
 * @param {DateTimeConfiguration} dateTimeConfiguration
 * @param {Record<string, unknown>} editorTranslation CkEditor custom
 *   translation
 * @returns
 */
export function buildLocaleBundle(
  language,
  churroLanguageBundle,
  pinataLanguageBundle,
  dateTimeConfiguration,
  editorTranslation,
) {
  const originalMessages = flattenAndCombineMessages(
    churroLanguageBundle.messages,
    pinataLanguageBundle.messages,
  );

  const messages = originalMessages;

  currentLocaleBundle = {
    ...defaultLocaleBundle,
    locale: language,
    locales: [language],
    language,
    messages,
    dateTimeConfiguration,
    editorTranslation,
  };
  return currentLocaleBundle;
}

function flattenAndCombineMessages(churroMessages, pinataMessages) {
  const flattenedChurroMessages = flattenMessages(churroMessages);
  const flattenedPinataMessages = flattenMessages(pinataMessages);
  return Object.assign(flattenedChurroMessages, flattenedPinataMessages);
}

export function getLocaleBundle() {
  return currentLocaleBundle;
}

function getSupportedLocale(locale, data) {
  if (data.hasOwnProperty(locale)) {
    return locale;
  }

  const genericLocale = locale.replace(/-.+$/, '');
  if (data.hasOwnProperty(genericLocale)) {
    return genericLocale;
  }
}

/**
 * @typedef {{
 *   profileDateFormat?: any;
 *   profileDateTimeFormat?: any;
 *   useLocaleFormat?: boolean;
 *   renderInWorkspaceTimeZone?: boolean;
 *   timeZone?: string;
 * }} DateTimeConfiguration
 */

/**
 * TO-DO: S253734 workspace config from window.envConfig is unreliable, fetch
 * directly from API
 *
 * @param {EnvConfig} config
 * @returns {DateTimeConfiguration}
 */
function getDateTimeConfigurationFromConfigData(config) {
  const toggles = config.context
    ? config.context.context.featureToggles.toggles
    : window.FEATURE_TOGGLES;
  const workspace = config.scope?.workspace;
  const profileDateFormat =
    config.globalContext.user.UserProfile.DateFormat ||
    workspace?.WorkspaceConfiguration.DateFormat;
  const profileDateTimeFormat =
    config.globalContext.user.UserProfile.DateTimeFormat ||
    workspace?.WorkspaceConfiguration.DateTimeFormat;

  return {
    profileDateFormat,
    profileDateTimeFormat,
    useLocaleFormat: toggles.ENABLE_LOCALIZED_DATE_FORMAT || false,
    renderInWorkspaceTimeZone: true,
    timeZone: workspace?.WorkspaceConfiguration.TimeZone,
  };
}

function flattenMessages(messages, prefix = '') {
  return Object.keys(messages).reduce((acc, key) => {
    const value = messages[key];
    const prefixedKey = prefix ? `${prefix}/${key}` : key;

    if (typeof value === 'string') {
      acc[prefixedKey] = value;
    } else {
      Object.assign(acc, flattenMessages(value, prefixedKey));
    }

    return acc;
  }, {});
}
